import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { confirmPopup } from "primereact/confirmpopup";
import { Tag } from "primereact/tag";
import { toast } from "react-toastify";
import { usePageHeader } from "../../Components/Template/PageTitle/PageContext";
import PageBackLink from "../../Components/Custom/PageBackLink";
import PermissionCheck from "../../Components/PermissionCheck";
import { AppDialogFooter } from "../../Components/Dialogs/AppDialog/AppDialog";
import {
    DashlyAutoComplete,
    DashlyDateInput,
    DashlyDropdown,
    DashlyTextInput,
} from "../../Components/inputs";
import IconPicker from "../../Components/inputs/IconPicker/IconPicker";
import PrimeIcon from "../../Models/PrimeIcon";
import { IconService } from "../../Services/IconService";
import { AccessModuleService, IAccessModuleCatalogEntry } from "../../Services/AccessModuleService";
import { CustomerService } from "../../Services/customerService";
import MultiCustomerCheck from "../../Components/MultiCustomerCheck";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import { RESOURCE_LOCATION } from "../../Constants/resourceLocations";
import {
    IArticleCategoryLink,
    IKnowledgeArticleCrudModel,
    KnowledgeArticleService,
    toKnowledgeArticleCrudModel,
} from "../../Services/KnowledgeArticleService";
import { IKnowledgeCategoryModel, KnowledgeCategoryService } from "../../Services/KnowledgeCategoryService";
import { IMediaAssetModel, MEDIA_TYPE, MediaAssetService, checkUploadableFiles, isImageAttachment, isVideoAttachment } from "../../Services/MediaAssetService";
import {
    IKnowledgeArticleSectionModel,
    KnowledgeArticleSectionService,
    MOVE_DIRECTION,
    MoveDirection,
    SECTION_TYPE,
    SectionType,
} from "../../Services/KnowledgeArticleSectionService";
import ReferenceModel from "../../Models/ReferenceModel";
import { INLINE_IMAGE_PLACEHOLDER_SRC } from "../../Util/resolveInlineMediaImages";
import MediaManager from "./MediaManager";
import MediaDropZone from "./MediaDropZone";
import ArticleSectionEditor, { ArticleSectionChanges } from "./ArticleSectionEditor";
import DraftSectionEditor, { DraftSection } from "./DraftSectionEditor";
import AddSectionDialog from "./AddSectionDialog";
import {
    KNOWLEDGE_ARTICLE_STATUS,
    KNOWLEDGE_BASE_LIST_PATH,
    knowledgeArticleDetailPath,
    resolveKnowledgeArticleIconClass,
    unwrapCrudList,
    stripImgByDataAttribute,
} from "./knowledgeBaseConstants";
import KnowledgeArticleRelationsCard from "./KnowledgeArticleRelationsCard";
import { KnowledgeBaseGcpCreateDisabledBanner } from "./KnowledgeBaseGcpProjectGate";
import { useKnowledgeBaseCreateAccess } from "./useKnowledgeBaseCreateAccess";
import { useMediaUrlRefresh } from "./useMediaUrlRefresh";
import {
    DraftAttachment,
    clearKnowledgeArticleDraft,
    collectDraftBlobEntries,
    isKnowledgeArticleDraftEmpty,
    persistKnowledgeArticleDraftMetadata,
    restoreKnowledgeArticleDraft,
    syncDraftBlobs,
} from "./knowledgeArticleDraftStorage";
import { getStoredUserCustomerUuid } from "../../Util/secureStorageSession";
import "./KnowledgeArticleEditor.scss";

const EMPTY_ARTICLE: IKnowledgeArticleCrudModel = {
    title: "",
    status: KNOWLEDGE_ARTICLE_STATUS.DRAFT,
    featured: false,
};

const DRAFT_AUTOSAVE_DEBOUNCE_MS = 800;
const SECTION_AUTOSAVE_DEBOUNCE_MS = 800;

const parseTags = (tags?: string): string[] =>
    (tags ?? "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

const KnowledgeArticleEditor: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const isEditMode = Boolean(uuid);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setPageHeader, resetPageHeader } = usePageHeader();

    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const knowledgeCategoryService = useMemo(() => new KnowledgeCategoryService(), []);
    const accessModuleService = useMemo(() => new AccessModuleService(), []);
    const customerService = useMemo(() => new CustomerService(), []);
    const iconService = useMemo(() => new IconService(), []);

    const [model, setModel] = useState<IKnowledgeArticleCrudModel>(EMPTY_ARTICLE);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draftReady, setDraftReady] = useState(isEditMode);
    const [persistedCustomerUuid, setPersistedCustomerUuid] = useState<string | undefined>();
    const canManage = useMemo(() => PermissionCheck("KnowledgeBase.Manage"), []);
    // Creating, and moving an existing article onto another customer, needs a knowledge-base GCP project
    // at that customer: its media is stored in that customer's buckets. Saving without changing customer does not.
    const customerChanging = isEditMode && !!model.customerUuid && model.customerUuid !== persistedCustomerUuid;
    const customerGateEnabled = canManage && draftReady && (!isEditMode || customerChanging);
    const createAccessStatus = useKnowledgeBaseCreateAccess(model.customerUuid, customerGateEnabled);
    const createBlocked = customerGateEnabled && createAccessStatus === "blocked";
    const createPending = customerGateEnabled && createAccessStatus === "loading";

    const [categories, setCategories] = useState<IKnowledgeCategoryModel[]>([]);
    const [selectedCategoryUuids, setSelectedCategoryUuids] = useState<string[]>([]);
    // Categories picked by a name that does not exist yet. Nothing is created while typing: they are made
    // together with the article's save (see saveCategoryLinks), and leaving the page drops them.
    const [newCategoryNames, setNewCategoryNames] = useState<string[]>([]);
    const [existingCategoryLinks, setExistingCategoryLinks] = useState<IArticleCategoryLink[]>([]);

    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [tagValues, setTagValues] = useState<string[]>([]);

    const [accessModules, setAccessModules] = useState<IAccessModuleCatalogEntry[]>([]);
    const [customerOptionsRaw, setCustomerOptionsRaw] = useState<ReferenceModel[]>([]);
    // Only a multi-customer user (a customer with sub-customers) may pick the owning customer or share the
    // article globally — the backend forces every single-customer save to that user's own customer and drops
    // the global flag when there is nobody below to share with, so both controls are hidden entirely for
    // them rather than shown non-functional.
    const isMultiCustomer = useMemo(() => MultiCustomerCheck(), []);
    // Whether the currently selected owning customer has sub-customers, i.e. "global" means anything.
    const [canShareGlobally, setCanShareGlobally] = useState(false);
    // False until GET /can-share-globally has answered. Saving before that must not send global=false:
    // that would unshare an article the author did not touch.
    const [globalShareResolved, setGlobalShareResolved] = useState(false);
    const [icons, setIcons] = useState<PrimeIcon[]>([]);

    const knowledgeArticleSectionService = useMemo(() => new KnowledgeArticleSectionService(), []);
    const mediaAssetService = useMemo(() => new MediaAssetService(), []);
    const [sections, setSections] = useState<IKnowledgeArticleSectionModel[]>([]);
    const [mediaAssets, setMediaAssets] = useState<IMediaAssetModel[]>([]);
    const [addSectionDialogVisible, setAddSectionDialogVisible] = useState(false);
    const sectionsRef = useRef(sections);
    sectionsRef.current = sections;
    // Section autosave (existing article only) — see handleSectionChange / flushSectionSaves.
    const unsavedSectionUuidsRef = useRef<Set<string>>(new Set());
    const sectionSaveTimerRef = useRef<number>();
    const sectionSaveQueueRef = useRef<Promise<void>>(Promise.resolve());

    // Create-mode only: everything (sections, their media, plain attachments) is staged here and only
    // actually created/uploaded once the article itself is saved for the first time — see handleSave.
    // Editing an existing article skips all of this; ArticleSectionEditor/MediaManager push immediately.
    const [draftSections, setDraftSections] = useState<DraftSection[]>([]);
    const [draftAttachments, setDraftAttachments] = useState<DraftAttachment[]>([]);
    const draftSectionsRef = useRef(draftSections);
    draftSectionsRef.current = draftSections;
    const persistedBlobIdsRef = useRef<Set<string>>(new Set());
    const mediaSaveFailedRef = useRef(false);
    const persistGenerationRef = useRef(0);

    useEffect(
        () => () => {
            // Release any local object URLs for staged media if the user navigates away without saving.
            draftSectionsRef.current.forEach((section) => section.media.forEach((item) => URL.revokeObjectURL(item.previewUrl)));
        },
        []
    );

    // Restore a previous session's unsaved draft (create mode only), including staged photos/videos.
    useEffect(() => {
        if (isEditMode) {
            setDraftReady(true);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const restored = await restoreKnowledgeArticleDraft();
                if (cancelled || !restored) {
                    return;
                }
                setModel(restored.model ?? EMPTY_ARTICLE);
                setTagValues(restored.tagValues);
                setSelectedCategoryUuids(restored.selectedCategoryUuids);
                setNewCategoryNames(restored.newCategoryNames);
                setDraftSections(restored.sections);
                setDraftAttachments(restored.attachments);
                persistedBlobIdsRef.current = new Set(
                    collectDraftBlobEntries(restored.sections, restored.attachments).map((entry) => entry.id)
                );
                toast.info(t("knowledgeBase.form.draftRestored") as string);
                if (restored.missingMediaCount > 0) {
                    toast.warning(t("knowledgeBase.form.draftMediaRestoredPartial") as string);
                }
            } catch {
                // Corrupt/unreadable draft — ignore, start fresh rather than block the page.
            } finally {
                if (!cancelled) {
                    setDraftReady(true);
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode]);

    // Debounced autosave: metadata in secureLocalStorage, file bytes in IndexedDB (see draft storage).
    useEffect(() => {
        if (isEditMode || !draftReady) {
            return undefined;
        }
        const generation = ++persistGenerationRef.current;
        const timer = setTimeout(() => {
            if (isKnowledgeArticleDraftEmpty(model, draftSections, draftAttachments)) {
                persistedBlobIdsRef.current = new Set();
                void clearKnowledgeArticleDraft();
                return;
            }
            const entries = collectDraftBlobEntries(draftSections, draftAttachments);
            void syncDraftBlobs(
                entries,
                persistedBlobIdsRef.current,
                () => generation !== persistGenerationRef.current
            ).then(({ persistedIds, mediaFailed }) => {
                if (generation !== persistGenerationRef.current) {
                    return;
                }
                persistedBlobIdsRef.current = persistedIds;
                if (mediaFailed && !mediaSaveFailedRef.current) {
                    mediaSaveFailedRef.current = true;
                    toast.warning(t("knowledgeBase.form.draftMediaSaveError") as string);
                }
                if (!mediaFailed) {
                    mediaSaveFailedRef.current = false;
                }
                try {
                    persistKnowledgeArticleDraftMetadata(
                        model,
                        tagValues,
                        selectedCategoryUuids,
                        newCategoryNames,
                        draftSections,
                        draftAttachments
                    );
                } catch {
                    // Metadata store full/unavailable — keep blobs; next successful tick retries.
                }
            });
        }, DRAFT_AUTOSAVE_DEBOUNCE_MS);
        return () => {
            persistGenerationRef.current += 1;
            clearTimeout(timer);
        };
    }, [isEditMode, draftReady, model, tagValues, selectedCategoryUuids, newCategoryNames, draftSections, draftAttachments, t]);

    // Candidates a SLIDESHOW section can reuse instead of re-uploading — any already-uploaded PHOTO/VIDEO
    // on this article (a plain attachment, or already sitting in another section). TEXT-section inline
    // photos still appear in the attachments list, but stay out of this picker: assigning one to a
    // slideshow would unhook it from the body HTML. Filtered per-section (excluding its own current
    // items) inside ArticleSectionEditor.
    const textSectionUuids = useMemo(
        () => new Set(sections.filter((section) => section.sectionType === SECTION_TYPE.TEXT).map((section) => section.uuid)),
        [sections]
    );
    const reusableMediaAssets = useMemo(
        () =>
            mediaAssets.filter(
                (asset) =>
                    // Includes legacy attachments still tagged FILE from before content-based classification
                    // existed — assignToSection reclassifies + processes them server-side on pick.
                    (asset.mediaType === MEDIA_TYPE.PHOTO ||
                        asset.mediaType === MEDIA_TYPE.VIDEO ||
                        (asset.mediaType === MEDIA_TYPE.FILE && (isImageAttachment(asset) || isVideoAttachment(asset)))) &&
                    !(asset.sectionUuid && textSectionUuids.has(asset.sectionUuid))
            ),
        [mediaAssets, textSectionUuids]
    );

    const statusOptions = useMemo(
        () => [
            { label: t("knowledgeBase.status.draft"), value: KNOWLEDGE_ARTICLE_STATUS.DRAFT },
            { label: t("knowledgeBase.status.published"), value: KNOWLEDGE_ARTICLE_STATUS.PUBLISHED },
            { label: t("knowledgeBase.status.archived"), value: KNOWLEDGE_ARTICLE_STATUS.ARCHIVED },
        ],
        [t]
    );

    const categoriesForArticleCustomer = useMemo(
        () => categories.filter((category) => !model.customerUuid || !category.customerUuid || category.customerUuid === model.customerUuid),
        [categories, model.customerUuid]
    );

    const categoryNameOptions = useMemo(
        () => categoriesForArticleCustomer.map((category) => ({ value: category.name })),
        [categoriesForArticleCustomer]
    );

    const selectedCategoryNames = useMemo(
        () => [
            ...categories.filter((category) => selectedCategoryUuids.includes(category.uuid)).map((category) => category.name),
            ...newCategoryNames,
        ],
        [categories, selectedCategoryUuids, newCategoryNames]
    );

    const tagAutoCompleteOptions = useMemo(() => tagOptions.map((tag) => ({ value: tag })), [tagOptions]);

    const accessModuleOptions = useMemo(
        () => accessModules.map((module) => ({ label: t(module.nameTag), value: module.code })),
        [accessModules, t]
    );

    const customerOptions = useMemo(
        () => customerOptionsRaw.map((customer) => ({ label: customer.name ?? customer.uuid ?? "", value: customer.uuid ?? "" })),
        [customerOptionsRaw]
    );

    const backTarget = uuid ? knowledgeArticleDetailPath(uuid) : KNOWLEDGE_BASE_LIST_PATH;

    // A new article starts on the user's own customer — an article always belongs to one. Waits for the
    // draft restore so it never overrides a customer the user had already picked.
    useEffect(() => {
        if (!isMultiCustomer || isEditMode || !draftReady || model.customerUuid) {
            return;
        }
        const ownCustomerUuid = getStoredUserCustomerUuid();
        if (ownCustomerUuid && customerOptions.some((option) => option.value === ownCustomerUuid)) {
            setModel((current) => (current.customerUuid ? current : { ...current, customerUuid: ownCustomerUuid }));
        }
    }, [isMultiCustomer, isEditMode, draftReady, model.customerUuid, customerOptions]);

    useEffect(() => {
        if (!isMultiCustomer || !canManage) {
            setCanShareGlobally(false);
            setGlobalShareResolved(true);
            return;
        }
        let cancelled = false;
        setGlobalShareResolved(false);
        knowledgeArticleService
            .fetchCanShareGlobally(model.customerUuid)
            .then((response) => {
                if (!cancelled) {
                    const allowed = response.data.data === true;
                    setCanShareGlobally(allowed);
                    setGlobalShareResolved(true);
                    if (!allowed) {
                        setModel((current) => (current.global ? { ...current, global: false } : current));
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    // Leave the stored flag alone: a failed check must not unshare the article on save.
                    setGlobalShareResolved(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isMultiCustomer, canManage, knowledgeArticleService, model.customerUuid]);

    const loadReferenceData = useCallback(async () => {
        const [categoriesResponse, tagsResponse, modulesCatalog, customersResponse, iconCatalog] =
            await Promise.all([
                knowledgeCategoryService.fetchItems({ includeInactive: false }),
                knowledgeArticleService.fetchTags(),
                accessModuleService.getCatalog(),
                // Only a multi-customer user can actually use this list (see isMultiCustomer above) — skip the
                // call, and the cross-tenant customer-name exposure it would otherwise carry, for everyone else.
                isMultiCustomer ? customerService.fetchSelectItems() : Promise.resolve(null),
                iconService.getIcons(),
            ]);
        setCategories(unwrapCrudList<IKnowledgeCategoryModel>(categoriesResponse.data.data));
        setTagOptions(tagsResponse.data.data ?? []);
        setAccessModules(modulesCatalog);
        setCustomerOptionsRaw(customersResponse?.data.data ?? []);
        setIcons(iconCatalog);
    }, [
        knowledgeCategoryService,
        knowledgeArticleService,
        accessModuleService,
        customerService,
        iconService,
        isMultiCustomer,
    ]);

    // The article comes with its sections and its category links: one request.
    const loadArticle = useCallback(async () => {
        if (!uuid) {
            return;
        }
        setLoading(true);
        try {
            const response = await knowledgeArticleService.fetchItem(uuid);
            const article = response.data.data;
            if (article.manageable === false) {
                // Shared down from a customer above: readable, never editable.
                navigate(knowledgeArticleDetailPath(uuid), { replace: true });
                return;
            }
            const links = article.categories ?? [];
            setModel(toKnowledgeArticleCrudModel(article));
            setPersistedCustomerUuid(article.customerUuid);
            setTagValues(parseTags(article.tags));
            setSections(article.sections ?? []);
            setExistingCategoryLinks(links);
            setSelectedCategoryUuids(links.map((link) => link.categoryUuid));
        } catch {
            // No form for an article that did not load: it would save emptiness over the real one.
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [uuid, knowledgeArticleService, navigate]);

    // Every asset comes with the signed URLs the editor shows it with: one request for all the media.
    const loadMedia = useCallback(async () => {
        if (!uuid) {
            return;
        }
        try {
            const response = await mediaAssetService.fetchByArticle(uuid);
            setMediaAssets(response.data.data ?? []);
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaLoadError", t));
        }
    }, [uuid, mediaAssetService, t]);

    useMediaUrlRefresh(loadMedia);

    useEffect(() => {
        if (!canManage) {
            navigate(backTarget, { replace: true });
            return;
        }
        loadReferenceData().catch((error) => toast.error(resolveApiErrorMessage(error, "knowledgeBase.loadError", t)));
        if (isEditMode) {
            loadArticle();
            loadMedia();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, uuid]);

    useEffect(() => {
        setPageHeader({
            title: {
                // Not yet saved (isEditMode is only true once the article actually has a uuid) — a plain
                // ReactNode title so the "Concept" tag can sit right next to it, same spot the status badge
                // would occupy on an already-saved article's own pages.
                value: isEditMode ? (
                    t("knowledgeBase.editArticle")
                ) : (
                    <span className="knowledge-article-editor__header-title">
                        {t("knowledgeBase.newArticle")}
                        <Tag value={t("knowledgeBase.draftBadge") as string} severity="warning" />
                    </span>
                ),
                translationKey: false,
            },
        });
        return () => resetPageHeader();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, t]);

    const findCategoryByName = (name: string) =>
        categoriesForArticleCustomer.find((category) => category.name.toLowerCase() === name.toLowerCase());

    /** A name that is no category yet stays a name until the article is saved — nothing is created here. */
    const handleCategoryNamesChange = (names: string[]) => {
        const resolvedUuids: string[] = [];
        const pendingNames: string[] = [];
        for (const rawName of names) {
            const name = rawName.trim();
            if (!name) {
                continue;
            }
            const existing = findCategoryByName(name);
            if (existing) {
                if (!resolvedUuids.includes(existing.uuid)) {
                    resolvedUuids.push(existing.uuid);
                }
            } else if (!pendingNames.some((pending) => pending.toLowerCase() === name.toLowerCase())) {
                pendingNames.push(name);
            }
        }
        setSelectedCategoryUuids(resolvedUuids);
        setNewCategoryNames(pendingNames);
    };

    /**
     * Saves every section that changed since its last save. The saves run one after the other, in the order
     * they were asked for, so an older request can never land after — and overwrite — a newer one. What is
     * sent is read at the moment of sending: always the latest title, text and "show title" together.
     */
    const flushSectionSaves = useCallback((): Promise<void> => {
        window.clearTimeout(sectionSaveTimerRef.current);
        sectionSaveQueueRef.current = sectionSaveQueueRef.current.then(async () => {
            const sectionUuids = Array.from(unsavedSectionUuidsRef.current);
            unsavedSectionUuidsRef.current.clear();
            for (const sectionUuid of sectionUuids) {
                const section = sectionsRef.current.find((candidate) => candidate.uuid === sectionUuid);
                if (!section) {
                    continue;
                }
                try {
                    await knowledgeArticleSectionService.putItem(section.uuid, {
                        knowledgeArticleUuid: section.knowledgeArticleUuid,
                        title: section.title,
                        sectionType: section.sectionType,
                        content: section.sectionType === SECTION_TYPE.TEXT ? section.content : undefined,
                        showTitle: section.showTitle,
                    });
                } catch (error) {
                    // Still unsaved: it goes along with the next save.
                    unsavedSectionUuidsRef.current.add(sectionUuid);
                    toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
                }
            }
        });
        return sectionSaveQueueRef.current;
    }, [knowledgeArticleSectionService, t]);

    // Leaving the page within the debounce must not cost the last words typed.
    useEffect(
        () => () => {
            void flushSectionSaves();
        },
        [flushSectionSaves]
    );

    /** A section of an existing article is saved as the author works — shortly after they stop typing — and
     *  not by the article's own Save button. This page holds the one copy of every section's values. */
    const handleSectionChange = (sectionUuid: string, changes: ArticleSectionChanges) => {
        setSections((current) => current.map((section) => (section.uuid === sectionUuid ? { ...section, ...changes } : section)));
        unsavedSectionUuidsRef.current.add(sectionUuid);
        window.clearTimeout(sectionSaveTimerRef.current);
        sectionSaveTimerRef.current = window.setTimeout(flushSectionSaves, SECTION_AUTOSAVE_DEBOUNCE_MS);
    };

    const handleMediaChanged = (deletedAssetUuid?: string) => {
        loadMedia();
        if (!deletedAssetUuid) {
            return;
        }
        // A deleted inline photo leaves the text it was in. The backend already took it out of the stored
        // text; this does the same for the text on screen, which may hold changes that are not saved yet.
        sectionsRef.current
            .filter((section) => section.sectionType === SECTION_TYPE.TEXT && section.content?.includes(deletedAssetUuid))
            .forEach((section) =>
                handleSectionChange(section.uuid, {
                    content: stripImgByDataAttribute(section.content ?? "", "data-asset-uuid", deletedAssetUuid),
                })
            );
    };

    const handleMoveSection = async (sectionUuid: string, direction: MoveDirection) => {
        const index = sections.findIndex((section) => section.uuid === sectionUuid);
        const swapWith = direction === MOVE_DIRECTION.UP ? index - 1 : index + 1;
        if (index === -1 || swapWith < 0 || swapWith >= sections.length) {
            return;
        }
        try {
            await knowledgeArticleSectionService.moveSection(sectionUuid, direction);
            // Same swap the backend just made — no need to fetch (and overwrite) the sections for it.
            setSections((current) => {
                const next = [...current];
                [next[index], next[swapWith]] = [next[swapWith], next[index]];
                return next;
            });
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        }
    };

    const handleDeleteSection = async (sectionUuid: string) => {
        try {
            await knowledgeArticleSectionService.deleteItem(sectionUuid);
            unsavedSectionUuidsRef.current.delete(sectionUuid);
            setSections((current) => current.filter((section) => section.uuid !== sectionUuid));
            // The section's media went with it.
            loadMedia();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        }
    };

    const handleSectionCreated = (sectionUuid: string, title: string, sectionType: SectionType) => {
        setAddSectionDialogVisible(false);
        if (!uuid) {
            return;
        }
        // A new section always comes last; adding it here keeps the other sections' unsaved text untouched.
        setSections((current) => [
            ...current,
            { uuid: sectionUuid, knowledgeArticleUuid: uuid, title, sectionType, showTitle: true, displayOrder: current.length },
        ]);
    };

    const handleAddDraftSection = (title: string, sectionType: SectionType) => {
        setDraftSections((prev) => [
            ...prev,
            { localId: crypto.randomUUID(), sectionType, title, showTitle: true, content: "", media: [], pendingInlineImages: {} },
        ]);
        setAddSectionDialogVisible(false);
    };

    const handleDraftSectionChange = (localId: string, updates: Partial<DraftSection>) => {
        setDraftSections((prev) =>
            prev.map((section) => {
                if (section.localId !== localId) {
                    return section;
                }
                const next: DraftSection = { ...section, ...updates };
                if (updates.content !== undefined) {
                    next.pendingInlineImages = Object.fromEntries(
                        Object.entries(next.pendingInlineImages).filter(([pendingId]) =>
                            next.content.includes(`data-pending-upload-id="${pendingId}"`)
                        )
                    );
                }
                return next;
            })
        );
    };

    const handleDraftInlineImageInsert = (sectionLocalId: string, pendingId: string, file: File) => {
        setDraftSections((prev) =>
            prev.map((section) =>
                section.localId === sectionLocalId
                    ? { ...section, pendingInlineImages: { ...section.pendingInlineImages, [pendingId]: file } }
                    : section
            )
        );
    };

    const handleMoveDraftSection = (localId: string, direction: MoveDirection) => {
        setDraftSections((prev) => {
            const index = prev.findIndex((section) => section.localId === localId);
            if (index === -1) {
                return prev;
            }
            const swapWith = direction === MOVE_DIRECTION.UP ? index - 1 : index + 1;
            if (swapWith < 0 || swapWith >= prev.length) {
                return prev;
            }
            const next = [...prev];
            [next[index], next[swapWith]] = [next[swapWith], next[index]];
            return next;
        });
    };

    const handleDeleteDraftSection = (localId: string) => {
        setDraftSections((prev) => {
            const target = prev.find((section) => section.localId === localId);
            target?.media.forEach((item) => URL.revokeObjectURL(item.previewUrl));
            return prev.filter((section) => section.localId !== localId);
        });
    };

    const handleDraftAttachmentsSelected = async (files: File[]) => {
        const { accepted, violations } = await checkUploadableFiles(files);
        violations.forEach((violation) => toast.error(t(violation) as string));
        if (accepted.length === 0) {
            return;
        }
        setDraftAttachments((prev) => [
            ...prev,
            ...accepted.map((file) => ({ localId: crypto.randomUUID(), file })),
        ]);
    };

    const handleRemoveDraftAttachment = (localId: string) => {
        setDraftAttachments((prev) => prev.filter((attachment) => attachment.localId !== localId));
    };

    const handleRemoveDraftInlineImage = (sectionLocalId: string, pendingId: string) => {
        setDraftSections((prev) =>
            prev.map((section) => {
                if (section.localId !== sectionLocalId) {
                    return section;
                }
                const pendingInlineImages = { ...section.pendingInlineImages };
                delete pendingInlineImages[pendingId];
                return {
                    ...section,
                    pendingInlineImages,
                    content: stripImgByDataAttribute(section.content, "data-pending-upload-id", pendingId),
                };
            })
        );
    };

    const draftInlineAttachments = useMemo(
        () =>
            draftSections.flatMap((section) =>
                Object.entries(section.pendingInlineImages).map(([pendingId, file]) => ({
                    sectionLocalId: section.localId,
                    pendingId,
                    file,
                }))
            ),
        [draftSections]
    );

    /** Uploads every inline image still marked `data-pending-upload-id="<id>"` in a just-created TEXT
     *  section's content and rewrites the HTML to reference the real asset instead — the shape
     *  WysiwygEditor's own immediate-upload path produces: `data-asset-uuid` plus a placeholder `src` (the
     *  real, short-lived URL is filled in whenever the text is shown, see resolveInlineMediaImages). An image
     *  whose upload fails is taken out of the text — its blob: URL would be dead as soon as this page is
     *  gone — and reported, without aborting the rest. */
    const resolveDraftInlineImages = async (
        articleUuid: string,
        sectionUuid: string,
        content: string,
        pendingInlineImages: Record<string, File>
    ): Promise<{ content: string; hadFailure: boolean }> => {
        let resolvedContent = content;
        let hadFailure = false;
        for (const [localId, file] of Object.entries(pendingInlineImages)) {
            if (!resolvedContent.includes(localId)) {
                continue;
            }
            try {
                const uploadResponse = await mediaAssetService.uploadMediaAsset(articleUuid, MEDIA_TYPE.PHOTO, file, sectionUuid);
                const assetUuid = uploadResponse.data.data;
                const markerPattern = new RegExp(`<img([^>]*)data-pending-upload-id="${localId}"([^>]*)>`);
                resolvedContent = resolvedContent.replace(markerPattern, (tag) =>
                    tag
                        .replace(/src="blob:[^"]*"/, `src="${INLINE_IMAGE_PLACEHOLDER_SRC}"`)
                        .replace(`data-pending-upload-id="${localId}"`, `data-asset-uuid="${assetUuid}"`)
                );
            } catch (error) {
                hadFailure = true;
                resolvedContent = stripImgByDataAttribute(resolvedContent, "data-pending-upload-id", localId);
                toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaUploadError", t));
            }
        }
        return { content: resolvedContent, hadFailure };
    };

    /** Creates every staged section (in order) plus its staged media, then every staged plain attachment —
     *  only reached for a brand-new article, right after it gets its real uuid. A failure partway through
     *  (e.g. one upload fails) is reported but doesn't roll back what already succeeded — the article
     *  itself is real at this point, so the user can fix the rest from its (immediate-upload) edit view.
     *  Returns whether anything failed, so the caller can avoid a plain "saved" toast that would hide a
     *  partial failure from the user. */
    const createDraftContent = async (articleUuid: string): Promise<boolean> => {
        let hadFailure = false;
        for (const draft of draftSections) {
            try {
                const sectionResponse = await knowledgeArticleSectionService.postItem({
                    knowledgeArticleUuid: articleUuid,
                    title: draft.title,
                    sectionType: draft.sectionType,
                    content: draft.sectionType === SECTION_TYPE.TEXT ? draft.content : undefined,
                    showTitle: draft.showTitle,
                });
                const sectionUuid = sectionResponse.data.data as string;
                for (const item of draft.media) {
                    try {
                        await mediaAssetService.uploadMediaAsset(articleUuid, item.mediaType, item.file, sectionUuid);
                    } catch (error) {
                        hadFailure = true;
                        toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaUploadError", t));
                    }
                }
                if (draft.sectionType === SECTION_TYPE.TEXT && Object.keys(draft.pendingInlineImages).length > 0) {
                    const resolved = await resolveDraftInlineImages(articleUuid, sectionUuid, draft.content, draft.pendingInlineImages);
                    hadFailure = hadFailure || resolved.hadFailure;
                    await knowledgeArticleSectionService.putItem(sectionUuid, {
                        knowledgeArticleUuid: articleUuid,
                        title: draft.title,
                        sectionType: draft.sectionType,
                        content: resolved.content,
                        showTitle: draft.showTitle,
                    });
                }
            } catch (error) {
                hadFailure = true;
                toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
            }
        }
        for (const attachment of draftAttachments) {
            try {
                await mediaAssetService.uploadMediaAsset(articleUuid, MEDIA_TYPE.FILE, attachment.file);
            } catch (error) {
                hadFailure = true;
                toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaUploadError", t));
            }
        }
        return hadFailure;
    };

    /**
     * Brings the article's category links in line with the selection; a category picked by a new name is
     * created here first. Returns whether anything failed.
     */
    const saveCategoryLinks = async (articleUuid: string): Promise<boolean> => {
        let hadFailure = false;
        const wantedUuids = [...selectedCategoryUuids];
        for (const name of newCategoryNames) {
            // Made in the meantime (a restored draft, another tab): link that one instead of a second one.
            const existing = findCategoryByName(name);
            if (existing) {
                wantedUuids.push(existing.uuid);
                continue;
            }
            try {
                const response = await knowledgeCategoryService.postItem({ name, customerUuid: model.customerUuid });
                wantedUuids.push(response.data.data as string);
            } catch (error) {
                hadFailure = true;
                toast.error(resolveApiErrorMessage(error, "knowledgeBase.categoryCreateError", t));
            }
        }
        const existingUuids = existingCategoryLinks.map((link) => link.categoryUuid);
        const toAdd = wantedUuids.filter(
            (categoryUuid, index) => wantedUuids.indexOf(categoryUuid) === index && !existingUuids.includes(categoryUuid)
        );
        const toRemove = existingCategoryLinks.filter((link) => !wantedUuids.includes(link.categoryUuid));
        const results = await Promise.allSettled([
            ...toAdd.map((categoryUuid) => knowledgeArticleService.link(articleUuid, RESOURCE_LOCATION.KNOWLEDGE_CATEGORY, categoryUuid)),
            ...toRemove.map((link) => knowledgeArticleService.unlink(link.resourceLinkUuid)),
        ]);
        return hadFailure || results.some((result) => result.status === "rejected");
    };

    const handleSave = async () => {
        if (createBlocked || createPending || saving) {
            return;
        }
        if (!model.title.trim()) {
            toast.error(t("knowledgeBase.form.validationError") as string);
            return;
        }
        setSaving(true);
        const payload: IKnowledgeArticleCrudModel = {
            ...model,
            tags: tagValues.join(", "),
            // Omit while the check is still in flight or failed: the backend then keeps the stored flag
            // and drops it itself when the owning customer has nobody below to share with.
            global: globalShareResolved ? !!model.global && canShareGlobally : undefined,
        };
        let articleUuid = uuid;
        try {
            if (articleUuid) {
                // The text typed in the last moment goes first: the article page opens right after this.
                await flushSectionSaves();
                await knowledgeArticleService.putItem(articleUuid, payload);
            } else {
                const response = await knowledgeArticleService.postItem(payload);
                articleUuid = response.data.data as string;
            }
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
            setSaving(false);
            return;
        }

        // From here on the article exists. Whatever fails below is reported, but never leaves the user on the
        // "new article" form: saving that again would create the same article a second time.
        let hadFailures = false;
        if (!uuid) {
            hadFailures = await createDraftContent(articleUuid);
            // Any retry for a failed section/attachment happens from the article's edit view, not by restoring
            // this draft — keeping the local backup around would only risk a duplicate article later.
            persistedBlobIdsRef.current = new Set();
            await clearKnowledgeArticleDraft();
        }
        hadFailures = (await saveCategoryLinks(articleUuid)) || hadFailures;

        // A partial failure already showed its own error toast(s) — a plain "saved" on top would bury them.
        if (hadFailures) {
            toast.warn(t("knowledgeBase.savedWithErrors") as string);
        } else {
            toast.success(t("knowledgeBase.saved") as string);
        }
        setSaving(false);
        navigate(knowledgeArticleDetailPath(articleUuid));
    };

    const handleCancel = () => navigate(backTarget);

    /** Throws the locally kept draft of a new article away, staged photos and videos included. */
    const handleDiscardDraft = () => {
        confirmPopup({
            // The footer button that was just clicked.
            target: document.activeElement as HTMLElement,
            message: t("knowledgeBase.form.draftDiscardConfirm") as string,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("knowledgeBase.form.draftDiscard") as string,
            rejectLabel: t("common.action.cancel") as string,
            acceptClassName: "p-button-danger",
            accept: discardDraft,
        });
    };

    const discardDraft = async () => {
        persistGenerationRef.current += 1;
        draftSections.forEach((section) => section.media.forEach((item) => URL.revokeObjectURL(item.previewUrl)));
        persistedBlobIdsRef.current = new Set();
        await clearKnowledgeArticleDraft();
        setModel(EMPTY_ARTICLE);
        setTagValues([]);
        setSelectedCategoryUuids([]);
        setNewCategoryNames([]);
        setDraftSections([]);
        setDraftAttachments([]);
    };

    if (loadFailed) {
        return (
            <div className="knowledge-article-editor">
                <PageBackLink to={KNOWLEDGE_BASE_LIST_PATH} label={t("knowledgeBase.backToList") as string} />
                <div className="knowledge-article-editor__card">
                    <p>{t("knowledgeBase.notAvailable")}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="knowledge-article-editor knowledge-article-editor--loading">
                <i className="pi pi-spinner pi-spin" />
                <span>{t("common.loading")}</span>
            </div>
        );
    }

    return (
        <div className="knowledge-article-editor">
            <PageBackLink to={backTarget} label={t(isEditMode ? "knowledgeBase.backToArticle" : "knowledgeBase.backToList") as string} />
            {createBlocked && <KnowledgeBaseGcpCreateDisabledBanner />}

            <div className="knowledge-article-editor__header">
                <IconPicker
                    id="knowledge-article-editor-icon"
                    className="knowledge-article-editor__icon-picker"
                    value={resolveKnowledgeArticleIconClass(model.icon)}
                    icons={icons}
                    title={t("knowledgeBase.form.iconHint") as string}
                    aria-label={t("common.icon") as string}
                    onChange={(event: { value?: string } | string) => {
                        const next = typeof event === "string" ? event : event?.value;
                        setModel({ ...model, icon: next || undefined });
                    }}
                />
                <div className="knowledge-article-editor__title-field">
                    <DashlyTextInput
                        className="knowledge-article-editor__title-input"
                        value={model.title}
                        onChange={(e) => setModel({ ...model, title: e.target.value })}
                        placeholder={t("knowledgeBase.form.titlePlaceholder") as string}
                        required
                    />
                </div>
            </div>

            <div className="knowledge-article-editor__layout">
                <div className="knowledge-article-editor__main">
                    <div className="knowledge-article-editor__card knowledge-article-editor__card--sections">
                        <span className="knowledge-article-editor__card-title">{t("knowledgeBase.form.sections")}</span>
                        {uuid ? (
                            <>
                                <div className="knowledge-article-editor__sections-list">
                                    {sections.map((section, index) => (
                                        <ArticleSectionEditor
                                            key={section.uuid}
                                            section={section}
                                            mediaAssets={mediaAssets}
                                            existingMediaAssets={reusableMediaAssets}
                                            knowledgeArticleUuid={uuid}
                                            isFirst={index === 0}
                                            isLast={index === sections.length - 1}
                                            onChange={(changes) => handleSectionChange(section.uuid, changes)}
                                            onMove={(direction) => handleMoveSection(section.uuid, direction)}
                                            onDelete={() => handleDeleteSection(section.uuid)}
                                            onMediaChanged={handleMediaChanged}
                                        />
                                    ))}
                                </div>
                                <Button
                                    type="button"
                                    label={t("knowledgeBase.form.addSection") as string}
                                    icon="pi pi-plus"
                                    outlined
                                    onClick={() => setAddSectionDialogVisible(true)}
                                />
                                <AddSectionDialog
                                    visible={addSectionDialogVisible}
                                    knowledgeArticleUuid={uuid}
                                    onHide={() => setAddSectionDialogVisible(false)}
                                    onCreated={handleSectionCreated}
                                />
                            </>
                        ) : (
                            <>
                                {/* Nothing here is sent to the backend until the article's own Save — see
                                    createDraftContent. Editing an existing article (the branch above)
                                    keeps pushing every change immediately, unchanged. */}
                                <div className="knowledge-article-editor__sections-list">
                                    {draftSections.map((section, index) => (
                                        <DraftSectionEditor
                                            key={section.localId}
                                            section={section}
                                            isFirst={index === 0}
                                            isLast={index === draftSections.length - 1}
                                            onChange={(updates) => handleDraftSectionChange(section.localId, updates)}
                                            onInlineImageInsert={(pendingId, file) =>
                                                handleDraftInlineImageInsert(section.localId, pendingId, file)
                                            }
                                            onMove={(direction) => handleMoveDraftSection(section.localId, direction)}
                                            onDelete={() => handleDeleteDraftSection(section.localId)}
                                        />
                                    ))}
                                </div>
                                <Button
                                    type="button"
                                    label={t("knowledgeBase.form.addSection") as string}
                                    icon="pi pi-plus"
                                    outlined
                                    onClick={() => setAddSectionDialogVisible(true)}
                                />
                                <AddSectionDialog
                                    visible={addSectionDialogVisible}
                                    onHide={() => setAddSectionDialogVisible(false)}
                                    onCreateLocal={handleAddDraftSection}
                                />
                            </>
                        )}
                    </div>

                    <div className="knowledge-article-editor__card">
                        <span className="knowledge-article-editor__card-title">{t("knowledgeBase.attachments")}</span>
                        {uuid ? (
                            <MediaManager knowledgeArticleUuid={uuid} mediaAssets={mediaAssets} onChanged={handleMediaChanged} />
                        ) : (
                            <div className="knowledge-article-editor__draft-attachments">
                                {(draftInlineAttachments.length > 0 || draftAttachments.length > 0) && (
                                    <ul className="knowledge-article-editor__draft-attachments-list">
                                        {draftInlineAttachments.map((attachment) => (
                                            <li key={`inline:${attachment.sectionLocalId}:${attachment.pendingId}`} className="knowledge-article-editor__draft-attachments-item">
                                                <i className="pi pi-image" />
                                                <span className="knowledge-article-editor__draft-attachments-name">{attachment.file.name}</span>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-trash"
                                                    text
                                                    rounded
                                                    onClick={() => handleRemoveDraftInlineImage(attachment.sectionLocalId, attachment.pendingId)}
                                                />
                                            </li>
                                        ))}
                                        {draftAttachments.map((attachment) => (
                                            <li key={attachment.localId} className="knowledge-article-editor__draft-attachments-item">
                                                <i className="pi pi-file" />
                                                <span className="knowledge-article-editor__draft-attachments-name">{attachment.file.name}</span>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-trash"
                                                    text
                                                    rounded
                                                    onClick={() => handleRemoveDraftAttachment(attachment.localId)}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <MediaDropZone
                                    accept="*/*"
                                    multiple
                                    hint={t("knowledgeBase.form.dropFileHint") as string}
                                    onFilesSelected={handleDraftAttachmentsSelected}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="knowledge-article-editor__side">
                    <div className="knowledge-article-editor__card">
                        <span className="knowledge-article-editor__card-title">{t("knowledgeBase.form.categories")}</span>
                        <DashlyAutoComplete
                            multiple
                            allowCustomValue
                            showSuggestionsOnFocus
                            options={categoryNameOptions}
                            optionLabel="value"
                            value={selectedCategoryNames}
                            onChange={handleCategoryNamesChange}
                            placeholder={t("knowledgeBase.form.categoriesPlaceholder") as string}
                        />
                    </div>

                    <div className="knowledge-article-editor__card">
                        <span className="knowledge-article-editor__card-title">{t("knowledgeBase.form.tags")}</span>
                        <DashlyAutoComplete
                            multiple
                            allowCustomValue
                            showSuggestionsOnFocus
                            options={tagAutoCompleteOptions}
                            optionLabel="value"
                            value={tagValues}
                            onChange={setTagValues}
                            placeholder={t("knowledgeBase.form.tagsPlaceholder") as string}
                        />
                    </div>

                    {isEditMode && uuid && canManage && (
                        <KnowledgeArticleRelationsCard
                            articleUuid={uuid}
                            sections={sections}
                            className="knowledge-article-editor__card"
                            titleClassName="knowledge-article-editor__card-title"
                        />
                    )}

                    <div className="knowledge-article-editor__card knowledge-article-editor__card--settings">
                        <span className="knowledge-article-editor__card-title">{t("knowledgeBase.form.settings")}</span>

                        {isMultiCustomer && (
                            <>
                                <span className="knowledge-article-editor__settings-group-label">
                                    {t("knowledgeBase.form.groupVisibility")}
                                </span>

                                <DashlyDropdown
                                    label={t("knowledgeBase.form.visibilityCustomer")}
                                    hint={t("knowledgeBase.form.visibilityHint") as string}
                                    options={customerOptions}
                                    value={model.customerUuid ?? null}
                                    onChange={(e) => {
                                        const customerUuid = e.value ?? undefined;
                                        setModel({ ...model, customerUuid });
                                        setSelectedCategoryUuids((current) =>
                                            current.filter((categoryUuid) => {
                                                const category = categories.find((item) => item.uuid === categoryUuid);
                                                return !customerUuid || !category?.customerUuid || category.customerUuid === customerUuid;
                                            })
                                        );
                                    }}
                                    filter
                                />

                                <div className="knowledge-article-editor__checkbox-row">
                                    <Checkbox
                                        inputId="knowledge-article-editor-global"
                                        checked={globalShareResolved ? !!model.global && canShareGlobally : !!model.global}
                                        disabled={!globalShareResolved || !canShareGlobally}
                                        onChange={(e) => setModel({ ...model, global: !!e.checked })}
                                    />
                                    <label htmlFor="knowledge-article-editor-global">{t("knowledgeBase.form.global")}</label>
                                </div>
                                <p className="knowledge-article-editor__field-hint">
                                    {t(!globalShareResolved || canShareGlobally ? "knowledgeBase.form.globalHint" : "knowledgeBase.form.globalUnavailableHint")}
                                </p>
                            </>
                        )}

                        <span className="knowledge-article-editor__settings-group-label knowledge-article-editor__settings-group-label--spaced">
                            {t("knowledgeBase.form.groupPublishing")}
                        </span>

                        <DashlyDropdown
                            label={t("knowledgeBase.form.status")}
                            options={statusOptions}
                            value={model.status}
                            onChange={(e) => setModel({ ...model, status: e.value })}
                        />

                        <DashlyDateInput
                            label={t("knowledgeBase.form.validFrom")}
                            showTime
                            value={model.validFrom ? new Date(model.validFrom) : null}
                            onChange={(e) => setModel({ ...model, validFrom: e.value ? (e.value as Date).toISOString() : undefined })}
                        />

                        <DashlyDateInput
                            label={t("knowledgeBase.form.expiresAt")}
                            showTime
                            value={model.expiresAt ? new Date(model.expiresAt) : null}
                            onChange={(e) => setModel({ ...model, expiresAt: e.value ? (e.value as Date).toISOString() : undefined })}
                        />

                        <span className="knowledge-article-editor__settings-group-label knowledge-article-editor__settings-group-label--spaced">
                            {t("knowledgeBase.form.groupDisplay")}
                        </span>

                        <DashlyDropdown
                            label={t("knowledgeBase.form.accessModuleCode")}
                            hint={t("knowledgeBase.form.accessModuleHint") as string}
                            options={accessModuleOptions}
                            value={model.accessModuleCode ?? null}
                            onChange={(e) => setModel({ ...model, accessModuleCode: e.value ?? undefined })}
                            showClear
                            filter
                            placeholder={t("knowledgeBase.form.accessModuleNone") as string}
                        />

                        <div className="knowledge-article-editor__checkbox-row">
                            <Checkbox
                                inputId="knowledge-article-editor-featured"
                                checked={!!model.featured}
                                onChange={(e) =>
                                    setModel({
                                        ...model,
                                        featured: !!e.checked,
                                        featuredExpiresAt: e.checked ? model.featuredExpiresAt : undefined,
                                    })
                                }
                            />
                            <label htmlFor="knowledge-article-editor-featured">{t("knowledgeBase.form.featured")}</label>
                        </div>

                        {!!model.featured && (
                            <DashlyDateInput
                                label={t("knowledgeBase.form.featuredExpiresAt")}
                                hint={t("knowledgeBase.form.featuredExpiresAtHint") as string}
                                showTime
                                value={model.featuredExpiresAt ? new Date(model.featuredExpiresAt) : null}
                                onChange={(e) =>
                                    setModel({
                                        ...model,
                                        featuredExpiresAt: e.value ? (e.value as Date).toISOString() : undefined,
                                    })
                                }
                            />
                        )}
                    </div>
                </div>
            </div>

            <AppDialogFooter
                className="knowledge-article-editor__footer"
                cancelLabel={t("common.action.cancel") as string}
                onCancel={handleCancel}
                cancelDisabled={saving}
                secondaryActions={
                    !isEditMode && !isKnowledgeArticleDraftEmpty(model, draftSections, draftAttachments)
                        ? [{
                            label: t("knowledgeBase.form.draftDiscard") as string,
                            icon: "pi pi-trash",
                            onClick: handleDiscardDraft,
                            disabled: saving,
                        }]
                        : undefined
                }
                submitLabel={t("knowledgeBase.save") as string}
                onSubmit={handleSave}
                submitDisabled={saving || createBlocked || createPending}
                submitLoading={saving}
            />
        </div>
    );
};

export default KnowledgeArticleEditor;
